import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/remix/$id/goals')({
  component: GoalsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    scanId: (search.scanId as string) || undefined,
  }),
})

type CosmeticGoal =
  | 'reduce_shine'
  | 'keep_glow'
  | 'lightweight_base'
  | 'even_redness'
  | 'soften_texture'

type SkinContext = {
  id: string
  scanId: string
  skinTone?: string
  skinType?: string
  concerns: string[]
  recommendations: string[]
  createdAt: string
}

type RemixPlan = {
  id: string
  userId: string
  lookId: string
  scanId?: string
  goals: CosmeticGoal[]
  status: string
  createdAt: string
  updatedAt: string
  skinContext?: SkinContext
}

const GOAL_OPTIONS: Array<{
  value: CosmeticGoal
  label: string
  description: string
}> = [
  { value: 'reduce_shine', label: 'Reduce Shine', description: 'Control excess oil and mattify' },
  { value: 'keep_glow', label: 'Keep Glow', description: 'Maintain natural radiance' },
  { value: 'lightweight_base', label: 'Lightweight Base', description: 'Sheer, natural coverage' },
  { value: 'even_redness', label: 'Even Redness', description: 'Color-correct and balance' },
  {
    value: 'soften_texture',
    label: 'Soften Texture',
    description: 'Smooth and blur imperfections',
  },
]

const CONCERN_LABELS: Record<string, string> = {
  dryness: 'Dryness',
  oiliness: 'Oiliness',
  uneven_tone: 'Uneven tone',
  redness: 'Redness',
  texture: 'Visible texture',
  pores: 'Visible pores',
  dark_spots: 'Dark spots',
  fine_lines: 'Fine lines',
}

function GoalsPage() {
  const { id } = Route.useParams()
  const { scanId } = Route.useSearch()
  const navigate = useNavigate()
  const [plan, setPlan] = React.useState<RemixPlan | null>(null)
  const [selectedGoals, setSelectedGoals] = React.useState<CosmeticGoal[]>([])
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const fetchPlan = React.useCallback(async () => {
    try {
      const res = await fetch(`/api/v1/remix-plans/${id}`)
      if (!res.ok) {
        if (res.status === 404) {
          setError('Remix plan not found. Please start from a look.')
          return
        }
        throw new Error('Failed to fetch plan')
      }
      const data = await res.json()
      setPlan(data)
      setSelectedGoals(data.goals ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    fetchPlan()
  }, [fetchPlan])

  function toggleGoal(goal: CosmeticGoal) {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal],
    )
  }

  async function handleContinue() {
    if (selectedGoals.length === 0) {
      setError('Please select at least one goal')
      return
    }

    setSaving(true)
    setError(null)

    try {
      // 1. Save goals (binds the scan to the plan for adapt + VTO)
      const goalsRes = await fetch(`/api/v1/remix-plans/${id}/goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goals: selectedGoals, scanId }),
      })

      if (!goalsRes.ok) {
        const data = await goalsRes.json()
        throw new Error(data.error ?? 'Failed to save goals')
      }

      // 2. Trigger adaptation
      const adaptRes = await fetch(`/api/v1/remix-plans/${id}/adapt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!adaptRes.ok) {
        const adaptData = await adaptRes.json()
        throw new Error(adaptData.error ?? 'Failed to adapt recipe')
      }

      // 3. Navigate to plan breakdown
      navigate({ to: '/remix/$id/plan', params: { id } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save goals')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted">Loading...</p>
      </div>
    )
  }

  if (error && !plan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-danger">{error}</p>
        <a href="/looks" className="text-primary hover:underline">
          Discover Looks
        </a>
      </div>
    )
  }

  return (
    <div className="ootopia-pwa-page ootopia-pwa-form">
      <div>
        <p className="ootopia-section-kicker">Shape the remix</p>
        <h1 className="mt-2 text-4xl font-black tracking-[-0.06em]">Choose what feels like you</h1>
        <p className="text-sm text-muted mt-1">
          Select what you'd like to achieve with your makeup look
        </p>
      </div>

      {/* Skin context summary */}
      {plan?.skinContext && (
        <div className="ootopia-pwa-surface ootopia-pwa-form__section p-4">
          <h2 className="font-semibold mb-3">Your Skin Context</h2>
          <div className="flex flex-wrap gap-2">
            {plan.skinContext.skinTone && (
              <span className="rounded-full bg-muted px-3 py-1 text-sm capitalize">
                {plan.skinContext.skinTone} tone
              </span>
            )}
            {plan.skinContext.skinType && (
              <span className="rounded-full bg-muted px-3 py-1 text-sm capitalize">
                {plan.skinContext.skinType}
              </span>
            )}
            {plan.skinContext.concerns.map((concern) => (
              <span key={concern} className="rounded-full bg-muted px-3 py-1 text-sm">
                {CONCERN_LABELS[concern] ?? concern}
              </span>
            ))}
          </div>
          {plan.scanId && (
            <a
              href={`/scan/${plan.scanId}`}
              className="text-primary text-sm mt-3 inline-block hover:underline"
            >
              View full analysis →
            </a>
          )}
        </div>
      )}

      {/* Goal selection */}
      <div className="space-y-3">
        <h2 className="font-semibold">Select Your Goals</h2>
        {GOAL_OPTIONS.map((goal) => {
          const isSelected = selectedGoals.includes(goal.value)
          return (
            <button
              type="button"
              key={goal.value}
              onClick={() => toggleGoal(goal.value)}
              className={`ootopia-pwa-option ${isSelected ? 'is-selected' : ''} ${
                isSelected
                  ? 'ootopia-gradient-accent text-accent-foreground'
                  : 'ootopia-card-premium hover:scale-[1.01]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="h-3 w-3 text-primary-foreground"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <title>Selected</title>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium">{goal.label}</p>
                  <p className="text-sm text-muted mt-0.5">{goal.description}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Error state */}
      {error && <div className="rounded-lg bg-danger/10 p-3 text-sm text-danger">{error}</div>}

      {/* Continue button */}
      <div className="sticky bottom-0 ootopia-glass border-t border-border/50 pt-4 pb-4 -mx-4 px-4">
        <button
          type="button"
          onClick={handleContinue}
          disabled={saving || selectedGoals.length === 0}
          className={`w-full rounded-xl py-3 font-medium transition-all ootopia-tap-target ${
            selectedGoals.length > 0
              ? 'ootopia-gradient-accent'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {saving
            ? 'Adapting...'
            : selectedGoals.length === 0
              ? 'Select at least one goal'
              : `Continue with ${selectedGoals.length} goal${selectedGoals.length > 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  )
}
